import React, { useState, useEffect, useRef } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import chatService from '@/services/chatService';
import { useLanguage } from '@/contexts/LanguageContext';
import './ChatWindow.css';

const ChatWindow = ({ onClose, isWidget = false }) => {
  const { language, direction, t, toggleLanguage } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [agentState, setAgentState] = useState('listening'); // Track agent state
  const [showWelcome, setShowWelcome] = useState(true); // Track if showing welcome screen
  const [isWideMode, setIsWideMode] = useState(false); // Track width mode (440 or 660)
  const [isLargeFontMode, setIsLargeFontMode] = useState(false); // Track font size (15 or 20)
  const [showMenu, setShowMenu] = useState(false); // Track menu visibility
  const menuRef = useRef(null); // Ref for menu container

  // Handler to start conversation from welcome screen
  const handleStartConversation = () => {
    setShowWelcome(false);
  };

  // Handler to toggle width mode
  const handleToggleWidth = () => {
    setIsWideMode(prev => !prev);
  };

  // Handler to toggle font size
  const handleToggleFontSize = () => {
    setIsLargeFontMode(prev => !prev);
  };

  // Handler to toggle menu
  const handleToggleMenu = () => {
    setShowMenu(prev => !prev);
  };

  // Handler to go back to welcome screen
  const handleBackToWelcome = () => {
    setShowWelcome(true);
    setMessages([]); // Clear messages
    setIsLoading(false);
    setShowMenu(false); // Close menu if open
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  // Handler to start a new session
  const handleNewSession = async () => {
    console.log('🆕 [ChatWindow] User requested new session');
    setMessages([]); // Clear UI immediately
    setIsLoading(false);
    setIsConnecting(true);
    
    try {
      const result = await chatService.startNewSession();
      if (result.success) {
        console.log('✅ [ChatWindow] New session started successfully');
      }
    } catch (error) {
      console.error('❌ [ChatWindow] Failed to start new session:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  // Initialize LiveKit connection when conversation starts
  useEffect(() => {
    // Don't initialize if still showing welcome screen
    if (showWelcome) {
      return;
    }

    let unsubscribeMessages = null;
    let unsubscribeSessionReset = null;
    let unsubscribeAgentState = null;
    let isMounted = true;

    const initializeChat = async () => {
      try {
        setIsConnecting(true);
        
        // Listen for session reset events (new LiveKit session)
        unsubscribeSessionReset = chatService.onSessionReset((sessionInfo) => {
          console.log('🆕 [ChatWindow] New session started, clearing messages:', sessionInfo);
          if (isMounted) {
            setMessages([]); // Clear all messages for new session
            setIsLoading(false);
            setAgentState('listening'); // Reset agent state
            console.log('✅ [ChatWindow] Messages cleared for new session');
          }
        });
        
        // Listen for agent state changes
        unsubscribeAgentState = chatService.onAgentStateChange((state) => {
          console.log('🤖 [ChatWindow] Agent state changed to:', state);
          if (isMounted) {
            setAgentState(state);
            // When agent finishes speaking/thinking, stop loading
            if (state === 'listening') {
              setIsLoading(false);
            }
          }
        });
        
        // Initialize and connect to LiveKit with current language
        const result = await chatService.connect(false, language);
        
        // Check if component is still mounted before updating state
        if (!isMounted) return;
        
        if (result.success) {
          console.log('✅ Chat service connected');
          
          // Listen for incoming messages
          unsubscribeMessages = chatService.onMessage((message) => {
            console.log('🎯 [ChatWindow] Received message in component:', message);
            console.log('🎯 [ChatWindow] Component mounted:', isMounted);
            if (isMounted) {
              console.log('🎯 [ChatWindow] Updating messages state');
              setMessages(prev => {
                console.log('🎯 [ChatWindow] Previous messages:', prev.length);
                const newMessages = [...prev, message];
                console.log('🎯 [ChatWindow] New messages:', newMessages.length);
                return newMessages;
              });
              // Don't set isLoading to false here - let agent state handle it
            } else {
              console.warn('⚠️ [ChatWindow] Component not mounted, skipping state update');
            }
          });
          console.log('✅ Message listener registered in ChatWindow');
        } else {
          console.error('❌ Failed to connect to chat service');
          // Show error message
          setMessages(prev => [...prev, {
            id: Date.now(),
            text: t('connectionError'),
            sender: 'system',
            timestamp: new Date().toISOString(),
          }]);
        }
      } catch (error) {
        console.error('❌ Chat initialization error:', error);
      } finally {
        if (isMounted) {
          setIsConnecting(false);
        }
      }
    };

    initializeChat();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      if (unsubscribeMessages) {
        unsubscribeMessages();
      }
      if (unsubscribeSessionReset) {
        unsubscribeSessionReset();
      }
      if (unsubscribeAgentState) {
        unsubscribeAgentState();
      }
      // IMPORTANT: Don't disconnect here!
      // The connection should persist across component mounts/unmounts
      // Only disconnect when the user explicitly closes the app/tab
      console.log('🔄 ChatWindow unmounting, keeping connection alive');
    };
  }, [showWelcome, language, t]); // Re-initialize if language changes or conversation starts

  const handleSendMessage = async (text) => {
    // Add user message to UI immediately with sending status
    const messageId = Date.now();
    const userMessage = {
      id: messageId,
      text,
      sender: 'user',
      timestamp: new Date().toISOString(),
      status: 'sending',
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Send message via LiveKit
      const result = await chatService.sendMessage(text);
      
      if (!result.success) {
        throw new Error('Failed to send message');
      }
      
      console.log('✅ Message sent successfully');
      
      // Update message status to sent
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, status: 'sent' } : msg
      ));
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      
      // Update message status to failed
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, status: 'failed' } : msg
      ));
      
      // Show error message
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: t('sendError'),
        sender: 'system',
        timestamp: new Date().toISOString(),
      }]);
      
      setIsLoading(false);
    }
  };

  return (
    <div className={`chat-window ${isWidget ? 'chat-window-widget' : 'chat-window-app'} ${isWideMode ? 'chat-window-wide' : ''} ${isLargeFontMode ? 'chat-window-large-font' : ''} ${showWelcome ? 'chat-window-welcome' : ''}`} dir={direction}>
      <div className="chat-header">
        <div className="chat-header-info">
          {!showWelcome && (
            <button 
              className="chat-back-button" 
              onClick={handleBackToWelcome}
              aria-label="Back to start"
              title="Back to start"
            >
              <img src="/assets/btn-back.svg" alt="Back" className="back-icon" />
            </button>
          )}
          <div className="chat-header-logo">
            <img 
              src={showWelcome ? "/assets/welcome-header-logo.svg" : "/assets/chat-header-logo.svg"} 
              alt="لبيه " 
              className="header-logo-icon" 
            />
          </div>
        </div>
        <div className="chat-header-actions">
          {!showWelcome && (
            <div className="chat-menu-container" ref={menuRef}>
              <button 
                className="chat-menu-button" 
                onClick={handleToggleMenu}
                aria-label="Toggle menu"
                title="Menu"
              >
                <img src="/assets/btn-menu.svg" alt="Menu" className="menu-icon" />
              </button>
              
              {showMenu && (
                <div className="chat-dropdown-menu">
                  <button 
                    className={`chat-toggle-font-button ${isLargeFontMode ? 'active' : ''}`}
                    onClick={handleToggleFontSize}
                    aria-label="Toggle font size"
                    title="Toggle font size"
                  >
                    <img src="/assets/btn-font.svg" alt="Toggle font size" className="font-icon" />
                  </button>
                  <button 
                    className={`chat-toggle-width-button ${isWideMode ? 'active' : ''}`}
                    onClick={handleToggleWidth}
                    aria-label="Toggle width"
                    title="Toggle width"
                  >
                    <img src="/assets/btn-size.svg" alt="Toggle width" className="size-icon" />
                  </button>
                  <button 
                    className="chat-new-session-button" 
                    onClick={handleNewSession}
                    aria-label={t('newSession')}
                    title={t('newSession')}
                  >
                    <img src="/assets/refresh.svg" alt={t('newSession')} className="refresh-icon" />
                  </button>
                </div>
              )}
            </div>
          )}
          {isWidget && showWelcome && (
            <button 
              className="chat-close-button" 
              onClick={() => {
                console.log('🔴 Close button clicked!');
                console.log('🔴 onClose function:', onClose);
                if (onClose && typeof onClose === 'function') {
                  onClose();
                } else {
                  console.error('❌ onClose is not a function!', onClose);
                }
              }}
              aria-label={t('closeChat')}
              title={t('closeChat')}
            >
              <img src="/assets/btn-close-widget.svg" alt="Close" className="close-icon" />
            </button>
          )}
        </div>
      </div>
      
      {showWelcome ? (
        <div className="chat-welcome-container">
          <div className="chat-welcome-content">
            <p className="chat-welcome-description">{t('welcomeDescription')}</p>
            
            <button 
              className="chat-welcome-start-button"
              onClick={handleStartConversation}
            >
              {t('startConversation')}
            </button>
            
            <div className="chat-welcome-language-toggle">
              <button 
                className="chat-language-toggle-button"
                onClick={toggleLanguage}
              >
                {language === 'ar' ? t('switchToEnglish') : t('switchToArabic')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <MessageList 
            messages={messages} 
            isLoading={isLoading} 
            isConnecting={isConnecting}
            agentState={agentState}
            onClickableTextClick={handleSendMessage}
            sessionId={chatService.roomName}
            userId={chatService.getUserUUID()}
            token={chatService.shareToken}
          />
          
          <MessageInput 
            onSendMessage={handleSendMessage} 
            disabled={isLoading || isConnecting}
          />
        </>
      )}
    </div>
  );
};

export default ChatWindow;
